import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  QueryStats as ResearchIcon,
  Psychology as InsightAiIcon,
  FilterListRounded as ScreenerIcon,
  CompareArrows as CompareArrowsIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as SuperInvestorsIcon,
  Calculate as ProjectionsIcon,
  RocketLaunch as RocketLaunchIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
  NotificationsNone as NotificationsIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import BottomSheet from "../common/BottomSheet";
import { useSafeNavigate } from "../../hooks/useSafeNavigate";
import MenuRow from "../common/MenuRow";
import HelpModal from "../auth/HelpModal";
import { useMembership } from "../../contexts/MembershipContext";
import { useLogout } from "../../hooks/useLogout";
import { hapticSelection, hapticImpact } from "../../lib/haptics";
import { markNavStart } from "../../lib/navPerf";
import NotificationCountChip from "../Notifications/NotificationCountChip";
import { useNotificationsContext } from "../Notifications/NotificationsProvider";
import { useInboxEnabled } from "../Notifications/hooks/useInboxEnabled";
import { useRecentNotificationCount } from "../Notifications/hooks/useRecentNotificationCount";

interface MoreSheetProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
  /** Membership-gated (matches the sidebar's Pro gating on Projections). */
  gated?: boolean;
}

/** Same destinations, icons and order as the sidebar drawer this replaces. */
const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", icon: <DashboardIcon fontSize="small" />, path: "/dashboard" },
  { label: "Research", icon: <ResearchIcon fontSize="small" />, path: "/charts" },
  { label: "Insight AI", icon: <InsightAiIcon fontSize="small" />, path: "/chat" },
  { label: "Screener", icon: <ScreenerIcon fontSize="small" />, path: "/screener" },
  { label: "Earnings", icon: <CalendarIcon fontSize="small" />, path: "/earnings" },
  { label: "Compare", icon: <CompareArrowsIcon fontSize="small" />, path: "/compare" },
  { label: "Investors", icon: <SuperInvestorsIcon fontSize="small" />, path: "/super-investors" },
  { label: "Projections", icon: <ProjectionsIcon fontSize="small" />, path: "/projections", gated: true },
];

/**
 * The native More surface: every app destination plus account actions, in the
 * app's bottom-sheet idiom. Replaces the old side drawer on native — a drawer
 * that slides under the status bar is a web idiom, while a sheet rising from
 * the tab that opened it is what iOS users expect. This component only ever
 * mounts on native (BottomTabBar renders solely inside NativeTabLayout), so
 * platform gating happens upstream, not here.
 */
const MoreSheet: React.FC<MoreSheetProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useSafeNavigate();
  const location = useLocation();
  const { isMembershipActive } = useMembership();
  const { mutate: logout } = useLogout();
  const [helpOpen, setHelpOpen] = useState(false);
  const { openInbox } = useNotificationsContext();
  const inboxEnabled = useInboxEnabled();
  const recentCount = useRecentNotificationCount();

  const isCurrent = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const handleNavigate = (path: string) => {
    hapticSelection();
    markNavStart("more_sheet");
    navigate(path);
    onClose();
  };

  // Mirrors UserProfileButton's Account Settings handler.
  const handleSettings = () => {
    hapticSelection();
    onClose();
    navigate("/auth/settings");
  };

  // Close first: leaving the sheet up behind the modal stacks two layers the
  // user has to dismiss in order (same convention as WatchlistSwitcher).
  const handleHelp = () => {
    hapticSelection();
    onClose();
    setHelpOpen(true);
  };

  // Same inbox the web profile menu opens (a sheet on native). Close first, as
  // with Help — never two sheets stacked.
  const handleNotifications = () => {
    hapticSelection();
    onClose();
    openInbox();
  };

  // Weightier haptic than a selection tick — signing out ends the session.
  const handleSignOut = () => {
    hapticImpact();
    onClose();
    logout();
  };

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title="Navigate">
        <Box role="menu">
          {NAV_ITEMS.map((item) => (
            <MenuRow
              key={item.path}
              icon={item.icon}
              label={item.label}
              selected={isCurrent(item.path)}
              disabled={Boolean(item.gated) && !isMembershipActive}
              disabledReason={item.gated ? "Available with Insight Pro" : undefined}
              onClick={() => handleNavigate(item.path)}
            />
          ))}
        </Box>

        {/* Upgrade CTA — non-members only, where the sidebar showed it. The
            native app sells no free trial, so no trial wording. */}
        {!isMembershipActive && (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => handleNavigate("/start-free-trial")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleNavigate("/start-free-trial");
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minHeight: 44,
              mt: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              backgroundColor: isDark ? "rgba(34, 197, 94, 0.12)" : "rgba(34, 197, 94, 0.08)",
              border: "1px solid rgba(34, 197, 94, 0.2)",
              WebkitTapHighlightColor: "transparent",
              transition: "transform 0.1s ease, background-color 0.1s ease",
              "&:active": {
                transform: "scale(0.98)",
                backgroundColor: isDark ? "rgba(34, 197, 94, 0.18)" : "rgba(34, 197, 94, 0.12)",
              },
              "&:focus-visible": {
                outline: "2px solid #22c55e",
                outlineOffset: -2,
              },
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                "&:active": { transform: "none" },
              },
            }}
          >
            <Box sx={{ display: "flex", flexShrink: 0, color: "#22c55e" }}>
              <RocketLaunchIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#22c55e" }}>
              Upgrade
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            my: 1.5,
            borderTop: `1px solid ${
              isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
            }`,
          }}
        />

        <Typography
          sx={{
            mb: 1,
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: isDark ? "#9ca3af" : "#64748b",
          }}
        >
          Account
        </Typography>

        <Box role="group">
          {inboxEnabled && (
            <MenuRow
              icon={<NotificationsIcon fontSize="small" />}
              label="Notifications"
              trailing={recentCount > 0 && <NotificationCountChip count={recentCount} />}
              onClick={handleNotifications}
            />
          )}
          <MenuRow
            icon={<SettingsIcon fontSize="small" />}
            label="Account Settings"
            onClick={handleSettings}
          />
          <MenuRow
            icon={<HelpIcon fontSize="small" />}
            label="Help"
            onClick={handleHelp}
          />
          <MenuRow
            icon={<LogoutIcon fontSize="small" />}
            label="Sign Out"
            destructive
            onClick={handleSignOut}
          />
        </Box>
      </BottomSheet>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default MoreSheet;
